import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { StaffSessionService } from '../services/staff-session.service';
import { StaffProfileService, StaffTeachingClass } from '../services/staff-profile.service';

declare var customToastr: any;
function safeToast(message: string, type: 'success' | 'error' | 'info' = 'info') {
  if (typeof customToastr === 'function') { customToastr(message, type); }
}

// View-another-staff-member's-profile screen, reached from search results.
// Reuses the same getTeacherInfo API as the self-profile screen (it already
// accepts any staffId), but has no logout button and no own-session cache
// pre-fill — everything here comes from the API response for the staffId
// in the route.
@Component({
  selector: 'app-staff-member-profile',
  templateUrl: './staff-member-profile.component.html',
  styleUrls: ['../shared/staff-common.css', '../profile/staff-profile.component.css']
})
export class StaffMemberProfileComponent implements OnInit {
  staffId: string = '';
  loading = true;
  loadError = false;

  staff = {
    name: '',
    initials: '',
    role: '',
    employeeId: '',
    staffImage: '',
    email: '',
    phone: '',
    address: '',
    gender: '',
    dateOfBirth: 'N/A',
    dateOfJoining: 'N/A',
    isClassTeacher: false,
  };

  teachingClasses: StaffTeachingClass[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private staffProfileService: StaffProfileService,
    private staffSession: StaffSessionService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.staffId = this.route.snapshot.paramMap.get('id') || '';
    this.loadProfile();
  }

  private async loadProfile() {
    if (!this.staffId) {
      this.loading = false;
      this.loadError = true;
      return;
    }

    this.loading = true;
    this.loadError = false;
    try {
      const response = await firstValueFrom(this.staffProfileService.getTeacherInfo(this.staffId));
      const responseDetails = response as any;
      const profile = responseDetails?.profile;
      if (!profile) {
        this.loadError = true;
        return;
      }

      this.staff.name = profile.staffName || '';
      this.staff.initials = this.staffSession.getInitials(this.staff.name);
      this.staff.role = profile.designation || '';
      this.staff.employeeId = profile.employmentId || '';
      this.staff.email = profile.emailId || '';
      this.staff.phone = profile.mobileNumber || '';
      this.staff.staffImage = profile.staffImage || '';
      this.staff.address = profile.address || '';
      this.staff.gender = profile.gender || '';
      this.staff.dateOfBirth = this.formatDate(profile.dateOfBirth);
      this.staff.dateOfJoining = this.formatJoiningDate(profile.dateOfJoining);
      // Unlike the self-profile screen, there's no cached "isClassTeacher"
      // flag for another staff member — classTeacherFor is the API's own
      // signal for it (non-empty when they're a class teacher for one or
      // more classes).
      this.staff.isClassTeacher = !!(responseDetails.classTeacherFor && responseDetails.classTeacherFor.length);

      this.teachingClasses = responseDetails.teachingClassesAndSubjects || [];
    } catch (error) {
      this.loadError = true;
      const unableToLoadProfileTranslation = await firstValueFrom(this.translate.get('unableToLoadProfilePleaseTryAgain'));
      safeToast(unableToLoadProfileTranslation, 'error');
    } finally {
      this.loading = false;
    }
  }

  private formatDate(value: string | null | undefined): string {
    if (!value) { return 'N/A'; }
    const d = new Date(value);
    if (isNaN(d.getTime())) { return 'N/A'; }
    return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  private formatJoiningDate(value: string | null | undefined): string {
    if (!value || value === '0000-00-00') { return 'N/A'; }
    return this.formatDate(value);
  }

  classLabel(c: StaffTeachingClass): string {
    return `${c.classNumber}-${c.classDivision} — ${c.subject}`;
  }

  goBack() {
    this.router.navigate(['/staff/search']);
  }
}
